import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Header } from "@/components/Header";
import { useScopedPath, useSlugScope } from "@/lib/scoped";
import { Archive, ChevronRight, Crown, Search, Trophy } from "lucide-react";
import { toast } from "sonner";

function formatPounds(pence) {
  if (!pence) return "£0.00";
  return `£${(pence / 100).toFixed(2)}`;
}

function shortDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function SeasonsArchivePage() {
  const scope = useScopedPath();
  const slug = useSlugScope();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // iter96 — slug-isolated Hall of Fame. When we're inside a
        // `/c/[slug]/*` client environment, pass the URL slug so the
        // backend filters the closure list to that tenant only.
        const url = slug
          ? `/champions?scope_slug=${encodeURIComponent(slug)}`
          : "/champions";
        const { data } = await api.get(url);
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((c) => {
      const name = (c.season_name || "").toLowerCase();
      const champ = ((c.champion || {}).name || "").toLowerCase();
      return name.includes(needle) || champ.includes(needle);
    });
  }, [q, rows]);

  const totals = useMemo(() => {
    const seasons = rows.length;
    return { seasons };
  }, [rows]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="seasons-archive-page">
        <div className="mb-10">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 inline-flex items-center gap-1.5">
            <Archive className="h-3.5 w-3.5" /> Archive
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-zinc-950 mt-1">
            Past seasons archive.
          </h1>
          <p className="text-base text-zinc-500 mt-3 max-w-2xl">
            Every closed season, with its champion and the final leaderboard preserved exactly as it stood at close-time. Click a row to drill into the full standings.
          </p>
        </div>

        {/* Headline stats */}
        {!loading && rows.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-testid="archive-stats">
            <div className="border border-zinc-200 rounded-sm p-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Seasons closed</p>
              <p className="font-heading text-3xl font-bold text-zinc-950 mt-1 tabular-nums" data-testid="archive-stats-seasons">
                {totals.seasons}
              </p>
            </div>
            <div className="border border-zinc-200 rounded-sm p-4 hidden sm:block">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Most recent</p>
              <p className="font-heading text-sm font-bold text-zinc-950 mt-2 truncate" data-testid="archive-stats-latest">
                {rows[0]?.season_name || "—"}
              </p>
              <p className="text-xs text-zinc-500 mt-1">{shortDate(rows[0]?.closed_at)}</p>
            </div>
          </div>
        )}

        {/* Search */}
        {!loading && rows.length > 0 && (
          <div className="mb-6 relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="archive-search"
              placeholder="Search by season or champion…"
              className="w-full pl-9 pr-3 h-10 rounded-sm border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950"
            />
          </div>
        )}

        {loading ? (
          <p className="text-sm uppercase tracking-[0.18em] text-zinc-500">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-sm p-16 text-center" data-testid="archive-empty">
            <p className="font-heading text-2xl font-semibold text-zinc-950">No closed seasons yet.</p>
            <p className="mt-2 text-sm text-zinc-500">
              Once the editor closes the current season, it&apos;ll show up here with its full final leaderboard.
            </p>
            <Link to={scope("/leaderboard")} className="inline-block mt-6 text-sm font-semibold text-[#0038FF] underline">
              Visit the current Overall League →
            </Link>
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-zinc-500" data-testid="archive-no-match">No seasons match &quot;{q}&quot;.</p>
        ) : (
          <ul className="divide-y divide-zinc-200 border-y border-zinc-200" data-testid="archive-list">
            {filtered.map((c) => {
              const champ = c.champion || {};
              return (
                <li key={c.id}>
                  <Link
                    to={scope(`/seasons-archive/${c.season_id || c.id}`)}
                    data-testid={`archive-row-${c.season_id || c.id}`}
                    className="group flex items-center gap-4 sm:gap-6 py-5 px-2 sm:px-3 hover:bg-zinc-50 transition-colors duration-150"
                  >
                    <span className="inline-flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-sm bg-gradient-to-b from-amber-400 to-amber-600 text-white shadow shrink-0">
                      <Crown className="h-5 w-5 sm:h-6 sm:w-6" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <p className="font-heading text-lg font-bold text-zinc-950 truncate" data-testid="archive-row-season-name">
                          {c.season_name || "Season"}
                        </p>
                        <p className="text-xs text-zinc-500">Closed {shortDate(c.closed_at)}</p>
                      </div>
                      <p className="text-sm text-zinc-700 mt-0.5 truncate">
                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mr-2">Champion</span>
                        <span data-testid="archive-row-champion-name">{champ.name || "—"}</span>
                        {champ.total_points != null && (
                          <span className="text-zinc-500"> · {champ.total_points} pts</span>
                        )}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-zinc-400 group-hover:text-zinc-950 transition-colors shrink-0" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-12">
          <Link to={scope("/champions")} className="text-sm font-semibold text-[#0038FF] underline inline-flex items-center gap-1.5" data-testid="archive-back-to-hof">
            <Trophy className="h-4 w-4" /> Back to the Hall of Fame
          </Link>
        </div>
      </main>
    </div>
  );
}
